import { useApolloClient } from "@apollo/client";
import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  Spacer,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { FC, useCallback, useState } from "react";
import { RiShieldLine } from "react-icons/ri";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { NotFound } from "src/components/Feedback/NotFound";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { AdminHeading } from "src/components/Text/AdminHeading";
import * as FSQ from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import useRequiredHasuraRoles from "src/hooks/useRequiredHasuraRoles";
import {
  isSchoolAdminVariaty,
  parseAccessPermission,
  SchoolType,
  TeamMemberFormSchema,
  TeamMemberFormType,
  toAccessPermission,
  UserGroupType,
} from "src/schemas/TeamMember";
import * as breadcrumb from "src/services/breadcrumb";
import { validateWithZod } from "src/services/formValidations";
import { isNonEmptyString } from "src/services/predicates";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";
import { HasuraRole } from "src/types/hasuraRole";
import { toTuple3 } from "src/types/remoteData";
import { TeamMemberForm } from "../components/Forms/TeamMemberForm";
import { EditUserProfile } from "../userProfile";
import { ArchiveTeamMemberButton } from "./ArchiveTeamMemberButton";
import {
  GET_AVAILABLE_TEAMS,
  GET_MEMBERS,
  GET_TEAM_MEMBER,
  GET_USER_GROUPS,
} from "./graphql/queries";
import { ResetMFAMenuItem } from "./ResetMFAMenuItem";
import { ResetPasswordMenuItem } from "./ResetPasswordMenuItem";

type EditTeamMemberFromData = {
  email_address: string;
  first_name: string;
  last_name: string;
  full_name: string;
  person_type: GQL.person_type_enum;
  user_group_id: string;
  selected_school_ids: string[];
  organization_schools: SchoolType[];
  user_roles: UserGroupType[];
  teams: GQL.GetAvailableTeams["team"];
  person_team_id: string;
  user_id: string | undefined;
  person_id: string;
  enable_attending_schools: boolean;
  enable_applying_schools: boolean;
  active: boolean;
};

type EditTeamMemberContainerProps = {
  data: EditTeamMemberFromData;
};

const EditTeamMemberContainer: FC<EditTeamMemberContainerProps> = ({
  data,
}) => {
  const toast = useAvelaToast();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const organization = useOrganization();

  const isAvelaAdmin = useRequiredHasuraRoles([HasuraRole.ADMIN]);

  const apolloClient = useApolloClient();

  const handleSubmit = useCallback(
    async (value: TeamMemberFormType) => {
      if (!data.active) return;

      setSubmitting(true);
      try {
        if (!organization.hasData()) throw Error("Invalid organization.");

        const selectedUserGroup = data.user_roles.find(
          (ug) => ug.id === value.user_group_id
        );
        if (!selectedUserGroup) throw new Error("no selected user group");

        const school_permissions = toAccessPermission(
          value,
          selectedUserGroup.person_type
        );
        const schoolIds = isSchoolAdminVariaty(selectedUserGroup)
          ? value.selected_school_ids
          : [];

        await FSQ.editTeamMember({
          path: {
            personId: data.person_id,
          },
          body: {
            firstName: value.first_name,
            lastName: value.last_name,
            schoolIds,
            userGroupId: value.user_group_id,
            schoolAccessPermission: school_permissions,
            teamId: isNonEmptyString(value.person_team_id)
              ? value.person_team_id
              : undefined,
          },
        });
        await apolloClient.refetchQueries({
          include: [GET_MEMBERS, GET_TEAM_MEMBER],
        });
        toast({
          title: "Team member updated",
          isClosable: true,
          status: "info",
        });

        navigate(Url.OrgAdmin.Team.index(organization.data));
      } catch (err) {
        const description = (): string => {
          if (err instanceof Error) {
            if (err.message.toLowerCase().includes("unauthorized")) {
              return "Not authorized to edit team member.";
            }
          }
          return "Check your network and try again.";
        };

        toast.error({
          title: `Something went wrong!`,
          description: description(),
          isClosable: true,
        });
      } finally {
        setSubmitting(false);
      }
    },
    [
      data.active,
      data.user_roles,
      data.person_id,
      organization,
      apolloClient,
      toast,
      navigate,
    ]
  );

  return (
    <Flex direction="column" height="100%">
      <Breadcrumb
        items={breadcrumb.team.getBreadcrumbsForEditMember(
          organization,
          data.person_id
        )}
        mb={8}
      />

      <Formik<TeamMemberFormType>
        initialValues={data}
        onSubmit={handleSubmit}
        validate={validateWithZod(TeamMemberFormSchema)}
      >
        {() => {
          return (
            <Flex as={Form} direction="column" gap={6} flexGrow="1" noValidate>
              <Flex direction="column" gap={0}>
                <AdminHeading
                  title="Edit team member"
                  id={isAvelaAdmin ? data.person_id : undefined}
                  actionButton={
                    <Flex direction="row" gap="2">
                      <ArchiveTeamMemberButton person={data} />
                      {data.active ? (
                        <Menu>
                          <MenuButton
                            as={Button}
                            leftIcon={<RiShieldLine />}
                            rightIcon={<ChevronDownIcon />}
                          >
                            Security
                          </MenuButton>
                          <MenuList>
                            <ResetMFAMenuItem person={data} />
                            <ResetPasswordMenuItem person={data} />
                          </MenuList>
                        </Menu>
                      ) : null}
                    </Flex>
                  }
                />
              </Flex>
              <TeamMemberForm
                allSchools={data.organization_schools}
                userGroups={data.user_roles}
                disableEmail={true}
                teams={data.teams}
                showTeam={true}
                active={data.active}
                disableRole={data.person_type === GQL.person_type_enum.admin}
              />
              <EditUserProfile
                personId={data.person_id}
                userId={data.user_id}
                refetchQueriesAfterDelink={[GET_TEAM_MEMBER]}
              />
              <Spacer />
              <AdminFormButtons>
                <Button
                  as={NavLink}
                  to={organization
                    .map((org) => Url.OrgAdmin.Team.index(org))
                    .withDefault("#")}
                  isLoading={organization.isLoading()}
                  variant="outline"
                  colorScheme="gray"
                >
                  Cancel
                </Button>
                <Spacer />
                <Button type="submit" isLoading={submitting}>
                  Update
                </Button>
              </AdminFormButtons>
            </Flex>
          );
        }}
      </Formik>
    </Flex>
  );
};

export const EditTeamMember: FC<{}> = () => {
  const { id = "" } = useParams();
  const organization = useOrganization();

  const { remoteData: teamMemberData } = useRemoteDataQuery<
    GQL.GetTeamMember,
    GQL.GetTeamMemberVariables
  >(GET_TEAM_MEMBER, {
    variables: {
      personID: id,
      organizationID: organization.map((org) => org.id).withDefault(""),
    },
    fetchPolicy: "no-cache",
  });

  const { remoteData: availableTeams } = useRemoteDataQuery<
    GQL.GetAvailableTeams,
    GQL.GetAvailableTeamsVariables
  >(GET_AVAILABLE_TEAMS, {
    variables: {
      organizationId: organization.map((org) => org.id).withDefault(""),
    },
    fetchPolicy: "cache-and-network",
  });

  const { remoteData: userGroupData } = useRemoteDataQuery<GQL.GetUserGroups>(
    GET_USER_GROUPS,
    { fetchPolicy: "no-cache" }
  );

  const remoteData = toTuple3(teamMemberData, userGroupData, availableTeams);

  return (
    <GQLRemoteDataView remoteData={remoteData}>
      {([teamMemberData, userGroupData, teamsData]) => {
        const person = teamMemberData.person[0];

        if (!person) {
          return <NotFound heading="" text="Member not found" />;
        }

        const school_permissions = parseAccessPermission(
          person.user?.school_users ?? []
        );

        const data = {
          person_id: person.id,
          email_address: person.email_address || "",
          first_name: person.first_name || "",
          last_name: person.last_name || "",
          full_name: person.full_name || "",
          user_group_id: person.user_group_id || "",
          person_type: person.person_type || GQL.person_type_enum.guardian,
          selected_user_ids: (person.user?.school_users ?? []).map((x) => {
            return x.school_id;
          }),
          organization_schools:
            teamMemberData.school.map((x) => {
              return { id: x.id, name: x.name } as SchoolType;
            }) || [],
          teams: teamsData.team,
          person_team_id: person.person_teams[0]?.team.id || "",
          user_roles: userGroupData.user_group,
          user_id: person.user?.id,
          active: person.active,
          selected_school_ids: (person.user?.school_users ?? []).map((x) => {
            return x.school_id;
          }),
          ...school_permissions,
        };

        return <EditTeamMemberContainer data={data}></EditTeamMemberContainer>;
      }}
    </GQLRemoteDataView>
  );
};
